import React, { useState, useEffect } from "react";
import TagManager from "react-gtm-module";
import ReactGA from "react-ga4";
import { Helmet } from "react-helmet";

// Helper function to get and parse the cookieConsent cookie
function getCookie(name) {
  const value = "; " + document.cookie;
  const parts = value.split("; " + name + "=");
  if (parts.length === 2) {
    try {
      let cookieValue = parts.pop().split(";").shift();
      if (cookieValue.includes("%")) {
        cookieValue = decodeURIComponent(cookieValue);
      }
      return JSON.parse(cookieValue);
    } catch (e) {
      console.error("Error parsing cookieConsent cookie:", e);
      return null;
    }
  }
  return null;
}

// Component responsible for initializing tracking scripts based on consent
const MetaPixel = () => {
  // State for consent status
  const [marketingConsentGiven, setMarketingConsentGiven] = useState(false);
  const [analyticsConsentGiven, setAnalyticsConsentGiven] = useState(false);

  // State for initialization flags to prevent re-initialization
  const [gaInitialized, setGaInitialized] = useState(false);
  const [gtmInitialized, setGtmInitialized] = useState(false);
  const [fbPixelInitialized, setFbPixelInitialized] = useState(false);

  const TRACKING_ID = "G-YK3SDP2HKL"; // Your GA4 Tracking ID
  const GTM_ID = "GTM-M33JZCB7";     // Your GTM ID
  const PIXEL_ID = "2248377412210776"; // Your Pixel ID

  // Effect to check consent on mount and listen for changes
  useEffect(() => {
    const checkConsent = () => {
      const consentData = getCookie("cookieConsent");
      const analyticsConsent = consentData?.analytics === true;
      const marketingConsent = consentData?.marketing === true;

      setAnalyticsConsentGiven(analyticsConsent);
      setMarketingConsentGiven(marketingConsent);

      // Log consent status for debugging
      // console.log("Consent Check:", { analyticsConsent, marketingConsent });
    };

    checkConsent(); // Initial check

    // Listener for consent changes from the banner
    const handleConsentChange = () => checkConsent();
    window.addEventListener("cookieConsentChanged", handleConsentChange);

    // Cleanup listener
    return () => {
      window.removeEventListener("cookieConsentChanged", handleConsentChange);
    };
  }, []); // Runs only on mount to set up listener

  // Effect for Google Analytics Initialization
  useEffect(() => {
    if (analyticsConsentGiven && !gaInitialized) {
      try {
        ReactGA.initialize(TRACKING_ID);
        ReactGA.send({
          hitType: "pageview",
          page: window.location.pathname + window.location.search, // Include search params
          title: document.title, // Use document title
        });
        setGaInitialized(true);
        // console.log("GA Initialized and Pageview Sent");
      } catch (error) {
        console.error("GA Initialization failed:", error);
      }
    }
    // Note: No easy 'uninitialize' for GA if consent is revoked.
  }, [analyticsConsentGiven, gaInitialized]); // Depends on consent and flag

  // Effect for Google Tag Manager Initialization
  useEffect(() => {
    if (analyticsConsentGiven && !gtmInitialized) {
       try {
        const tagManagerArgs = { gtmId: GTM_ID };
        TagManager.initialize(tagManagerArgs);
        setGtmInitialized(true);
        // console.log("GTM Initialized");
      } catch (error) {
         console.error("GTM Initialization failed:", error);
       }
    }
     // Note: No easy 'uninitialize' for GTM if consent is revoked.
  }, [analyticsConsentGiven, gtmInitialized]); // Depends on consent and flag

  // Effect for Facebook Pixel Initialization
  useEffect(() => {
    if (marketingConsentGiven && !fbPixelInitialized) {
       try {
        // Check if fbq function already exists to avoid re-injecting script
        if (!window.fbq) {
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
        }
         // Initialize and track page view
         window.fbq('init', PIXEL_ID);
         window.fbq('track', 'PageView');
         setFbPixelInitialized(true);
         // console.log("FB Pixel Initialized and PageView Tracked");
       } catch(error) {
         console.error("FB Pixel Initialization failed:", error);
       }
    }
     // Note: No easy 'uninitialize' for Pixel if consent is revoked.
  }, [marketingConsentGiven, fbPixelInitialized]); // Depends on consent and flag

  // Render only the noscript fallback for FB Pixel if marketing consent is given
  // The actual script initialization is handled in the useEffect above.
  return marketingConsentGiven ? (
    <Helmet>
      <noscript id="facebook-pixel-image">
        {`<img
            height="1"
            width="1"
            style="display:none"
            src="https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1"
          />`}
      </noscript>
    </Helmet>
  ) : null; // Render nothing if no marketing consent
};

export default MetaPixel;